import { ref, set, push, onValue, serverTimestamp,  query, orderByChild, startAt } from 'firebase/database';
 
class FirebaseEvents {
    constructor(database, userEmail) {
        // Initialize processedEvents as a standard Set
        // Clear it on construction to ensure fresh state after page reload
        this.processedEvents = new Set();
        this.database = database;
        this.userEmail = userEmail;
        this.listeners = new Map();
        this.eventRefs = new Map();
        this.rooms = new Set();
        this.sessionStartTime = Date.now(); // Reset session start time on new instance
        window.sessionStartTime = this.sessionStartTime; // Make available globally for message divider
        this.cacheExpirationTime = 5 * 60 * 1000; // 5 minutes
        this.eventCache = new Map();
        console.log('FirebaseEvents initialized with fresh session start time:', this.sessionStartTime);
        
        // Get database prefix from environment (fallback to 'dev' in development if unset)
        this.dbPrefix = process.env.REACT_APP_FIREBASE_DATABASE_PREFIX || (process.env.NODE_ENV === 'development' ? 'dev' : '');
        console.log('Firebase DB Prefix:', this.dbPrefix, 'from env:', process.env.REACT_APP_FIREBASE_DATABASE_PREFIX);
        
        window.sessionStartTime = this.sessionStartTime; // Make available globally
        
        // Bind methods to ensure correct 'this' context
        this.isEventStaleV2 = this.isEventStaleV2.bind(this);
        this.emit = this.emit.bind(this);
        this.on = this.on.bind(this);
        this.off = this.off.bind(this);
        this.cleanup = this.cleanup.bind(this);
        this.clearProcessedEventsForType = this.clearProcessedEventsForType.bind(this)
        
        // Don't clean up campaign state on initialization - causes disconnect/reconnect during active campaigns
        // cleanupCampaignState should only be called explicitly when needed, not automatically
    }

    encodeEmail(email) {
        if (!email) return '';
        // For emails like samuel@grupa.ai, we need to match backend encoding
        // Backend uses samuel@grupa_ai (only dots after @ are replaced)
        if (email.includes('@')) {
            const [localPart, domain] = email.split('@');
            // Replace dots in domain with underscores to match backend
            const encodedDomain = domain.replace(/\./g, '_');
            // Also replace other Firebase-invalid characters
            return `${localPart}@${encodedDomain}`.replace(/[#$[\]/]/g, '_');
        }
        return email;
    }

    decodeEmail(encodedEmail) {
        if (!encodedEmail) return '';
        // If it doesn't contain underscores, it's probably not encoded
        if (!encodedEmail.includes('_')) return encodedEmail;
        // Replace underscores with dots for email format
        return encodedEmail.replace(/_/g, '.');
    }

    // New simplified staleness check using only sortTime
    isEventStaleV2(data) {
        if (!data) return false;
        
        // If no sortTime, consider it fresh (realtime event)
        if (!data.sortTime) {
            console.debug('Event fresh: No sortTime (realtime event)');
            return false;
        }
        
        // For real-time messages, use current time as reference
        // Allow messages from the last 5 minutes to handle delays
        const now = Date.now();
        console.log('[FIREBASE] Staleness check:', { 
            now, 
            sortTime: data.sortTime, 
            diff: data.sortTime - now, 
            diffMinutes: (data.sortTime - now) / 60000,
            cutoff: now - 5*60*1000, 
            isOld: data.sortTime < (now - 5*60*1000), 
            isFuture: data.sortTime > (now + 60000),
            eventType: data.type,
            agentId: data.agent_id
        });
        const REALTIME_WINDOW_MS = 5 * 60 * 1000; // 5 minutes for delayed messages
        const FUTURE_TOLERANCE_MS = 60000; // 1 minute tolerance for clock skew
        
        // Message is stale if it's older than 5 minutes from now
        // Also reject if more than 1 minute in future (likely clock issue)
        const isStale = data.sortTime < (now - REALTIME_WINDOW_MS) || 
                        data.sortTime > (now + FUTURE_TOLERANCE_MS);
        
        if (isStale) {
            console.debug('Event stale (v2):', {
                eventType: data.type,
                sortTime: data.sortTime,
                cutoffTime: now - REALTIME_WINDOW_MS,
                currentTime: now
            });
        }
        
        return isStale;
    }

    isEventStale(data) {
        if (!data) {
            console.debug('Event stale: No data provided');
            return true;
        }

        // Special handling for agent initialization and campaign events (including system messages for campaigns)
        if (data.type === 'agents_initialized' || data.type === 'start_campaign' || 
            data.type === 'campaign_status' || data.type === 'campaign_started' ||
            data.type === 'resume_started' || data.eventName === 'resume_started' || // Add resume_started
            data.eventName === 'campaign_started' || // Check eventName field too
            data.event_name === 'initialize_agents' || // Backend uses event_name field
            data.event_name === 'agents_initialized' || // May also come as this
            data.event_name === 'start_campaign' || // Backend start campaign event
            (data.type === 'system' && data.campaign_id)) {
            
            // First check sortTime since it's immediately available
            if (data.sortTime) {
                return data.sortTime < this.sessionStartTime-300000; // 5 minute grace period for resume events
            }
            
            // Always require timestamp
            if (!data.timestamp) {
                console.debug('Event stale: Missing timestamp for agent/campaign event');
                return true;
            }

            // For campaign events, check if they're from the current session
            // Server timestamps (.sv: 'timestamp') are pending and should be considered fresh ONLY
            // if they're being written NOW. If we're reading them, they should have resolved to a number.
            if (typeof data.timestamp === 'object' && 
                (data.timestamp.sv === 'timestamp' || data.timestamp['.sv'] === 'timestamp')) {
                // This is a pending server timestamp - it means the event is being written now
                // For reading events, Firebase should have already resolved this to a number
                // If we're seeing this object, it's likely a fresh write
                console.debug('Event fresh: Has pending server timestamp');
                return false;
            }
            
            // Parse resolved timestamps
            let eventTime;
            if (typeof data.timestamp === 'number') {
                eventTime = data.timestamp;
            } else if (typeof data.timestamp === 'string') {
                eventTime = new Date(data.timestamp).getTime();
            } else {
                console.debug('Event stale: Unknown timestamp format for agent/campaign event');
                return true;
            }
            
            // Check if event is from before this session (with 10 second grace period for reload)
            const RELOAD_GRACE_PERIOD = 10 * 1000; // 10 seconds for messages sent during reload
            if (eventTime < this.sessionStartTime - 300000) { // 5 minute grace period for delayed events
                console.debug('Event stale: Campaign event from previous session', {
                    eventTime,
                    sessionStartTime: this.sessionStartTime,
                    gracePeriodStart: this.sessionStartTime - RELOAD_GRACE_PERIOD
                });
                return true;
            }

            // For start_campaign events, require userInitiated flag
            if (data.type === 'start_campaign' && !data.userInitiated) {
                console.debug('Event stale: Non-user-initiated campaign start');
                return true;
            }

            // If it's a start_campaign event, check if we already have a running campaign
            if (data.type === 'start_campaign') {
                const campaignStatus = window.campaignStatus || {};
                if (campaignStatus.isRunning) {
                    console.debug('Event stale: Campaign already running');
                    return true;
                }

                // Check if this is a stale start attempt (older than 30 seconds)
                const THIRTY_SECONDS = 30 * 1000;
                if (Date.now() - eventTime > THIRTY_SECONDS) {
                    console.debug('Event stale: Start campaign event too old');
                    return true;
                }
            }

            // For campaign_started events, use the already parsed eventTime
            if (data.type === 'campaign_started') {
                // Event time was already parsed and checked above
                // If we reach here, the event is from current session
                console.debug('Campaign started event is fresh');
                return false;
            }
            
            // For agents_initialized events, also check for additional staleness conditions
            if (data.type === 'agents_initialized') {
                // Check if success is false - these are failure events that shouldn't trigger UI
                if (data.success === false) {
                    console.debug('Event stale: Failed initialization event');
                    return true;
                }
                
                // Only consider fresh if from current session and successful
                console.debug('Agents initialized event is fresh');
                return false;
            }
        }

        // Special handling for email review requests to ensure proper ordering
        if (data.type === 'email_review_request') {
            // Always require timestamp for email reviews
            if (!data.timestamp) {
                console.debug('Email review stale: Missing timestamp');
                return true;
            }

            // Only show email reviews for the current active campaign AND current prospect
            if ((data.campaign_id && window.currentCampaignId && data.campaign_id !== window.currentCampaignId) || 
                (data.prospect_email && window.currentProspectEmail && data.prospect_email !== window.currentProspectEmail)) {
                console.debug('Email review stale: Different campaign or prospect', {
                    reviewCampaignId: data.campaign_id,
                    currentCampaignId: window.currentCampaignId,
                    reviewProspect: data.prospect_email,
                    currentProspect: window.currentProspectEmail
                });
                return true;
            }

            if (typeof data.timestamp === 'object' && (data.timestamp['.sv'] === 'timestamp' || data.timestamp['sv'] === 'timestamp')) return false; // treat pending server timestamp as fresh
            // Convert timestamp to ms; if not a number/string, treat as 0 (stale)
            const reviewTime = typeof data.timestamp === 'string' ? new Date(data.timestamp).getTime() : (typeof data.timestamp === 'number' ? data.timestamp : 0);

            // Skip old reviews - filter ALL reviews before session start
            const ACTIVE_REVIEW_WINDOW = 0; // No window - filter all pre-session reviews
            if (reviewTime < this.sessionStartTime) {
                console.debug('Email review stale: Too old', {
                    reviewTime,
                    sessionStartTime: this.sessionStartTime,
                    windowStart: this.sessionStartTime - ACTIVE_REVIEW_WINDOW
                });
                return true;
            }

            // Check if this review has already been processed
            const reviewKey = `email_review:${data.id}`;
            // Ensure processedEvents is a Set
            if (!this.processedEvents || !(this.processedEvents instanceof Set)) {
                console.warn('processedEvents is not a Set, reinitializing');
                this.processedEvents = new Set();
            }
            if (this.processedEvents.has(reviewKey)) {
                console.debug('Email review stale: Already processed', { id: data.id });
                return true;
            }

            return false;
        }

        // Handle chat messages - allow a recent history window across reloads
        if (data.type === 'group_chat_message' || data.type === 'message' || data.type === 'agent_message') {
            // Use a 5-minute window for chat messages to show recent history
            const CHAT_HISTORY_WINDOW = 5 * 60 * 1000; // 5 minutes
            const windowStart = Date.now() - CHAT_HISTORY_WINDOW;

            // First check sortTime since it's immediately available
            if (data.sortTime) {
                // Allow messages within the recent history window
                return data.sortTime < windowStart;
            }

            // Check if message has a timestamp
            if (!data.timestamp) {
                console.debug('Chat message fresh: No timestamp (realtime)');
                return false; // Allow realtime messages without timestamp
            }

            // Handle server timestamp object
            if (typeof data.timestamp === 'object' && 
                (data.timestamp['.sv'] === 'timestamp' || data.timestamp['.sv'] === 'timestamp')) {
                console.debug('Chat message fresh: Has server timestamp');
                return false; // Consider server timestamp as fresh
            }

            // Parse resolved timestamp
            const messageTime = typeof data.timestamp === 'string' ? 
                new Date(data.timestamp).getTime() : data.timestamp;

            // Allow messages within recent history window (covers page reloads)
            if (messageTime >= windowStart) {
                console.debug('Chat message fresh: Within history window', {
                    messageTime,
                    windowStart,
                    sessionStartTime: this.sessionStartTime
                });
                return false;
            }

            console.debug('Chat message stale: Outside history window', {
                messageTime,
                windowStart,
                sessionStartTime: this.sessionStartTime
            });
            return true;
        }

        // If it's a Firebase push ID object, get the latest event
        if (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).some(key => key.startsWith('-'))) {
            const events = Object.entries(data).map(([key, value]) => ({
                key,
                ...value
            }));
            events.sort((a, b) => (a.sortTime ?? 0) - (b.sortTime ?? 0) || a.key.localeCompare(b.key)); // Sort by sortTime, fallback to push ID
            
            // Get the latest event
            const latestEvent = events[events.length - 1];
            console.debug('Found latest event:', latestEvent);
            
            // Check the timestamp in the latest event
            return this.isEventStaleV2(latestEvent);
        }
        
        // If it's a server timestamp object directly
        if (typeof data === 'object' && (data['.sv'] === 'timestamp' || data['sv'] === 'timestamp')) {
            console.debug('Event fresh: Is server timestamp');
            return false;
        }

        // Special handling for metrics_update events - only process recent ones
        // Firebase stores these permanently, so we need to filter old ones
        if (data.type === 'metrics_update' || data.event_name === 'metrics_update') {
            if (data.sortTime) {
                const now = Date.now();
                const RECENT_WINDOW = 30 * 1000; // 30 seconds - enough for network delays
                const isRecent = data.sortTime > (now - RECENT_WINDOW);
                console.debug(`Metrics update ${isRecent ? 'recent' : 'old'}:`, {
                    sortTime: data.sortTime,
                    age: (now - data.sortTime) / 1000 + 's'
                });
                return !isRecent; // Stale if older than 30 seconds
            }
            // No sortTime means it's a real-time event
            return false;
        }

        // Check timestamp field
        const timestamp = data.timestamp;
        if (!timestamp) {
            console.debug('Event stale: No timestamp field');
            return true;
        }

        // If it's a pending server timestamp
        if (typeof timestamp === 'object' && 
            (timestamp['.sv'] === 'timestamp' || timestamp['sv'] === 'timestamp')) {
            console.debug('Event fresh: Has server timestamp');
            return false;
        }

        // For numeric timestamps
        if (typeof timestamp === 'number') {
            // Check if event is from before this session
            if (timestamp < this.sessionStartTime) {
                console.debug('Event stale: From previous session', {
                    eventTime: timestamp,
                    sessionStartTime: this.sessionStartTime
                });
                return true;
            }
            return false;
        }

        // For ISO string timestamps
        if (typeof timestamp === 'string') {
            const eventTime = new Date(timestamp).getTime();
            // Check if event is from before this session
            if (eventTime < this.sessionStartTime) {
                console.debug('Event stale: From previous session', {
                    eventTime,
                    sessionStartTime: this.sessionStartTime
                });
                return true;
            }
            return false;
        }

        return false; // Default to not stale for other event types
    }

    // Helper to get prefixed path
    getPath(path) {
        const prefixedPath = this.dbPrefix ? `${this.dbPrefix}/${path}` : path;
        console.log('Firebase path:', path, '-> prefixed:', prefixedPath);
        return prefixedPath;
    }

       // Helper to get prefixed path: TODO- This was problematic, not sure why it was not working- i guess its already prefixed
    //getPath(path) {
        // Check if we're in development/testing mode to prepend 'dev/'
    //    const isDevelopment = process.env.NODE_ENV === 'development';
    //    let prefixedPath = this.dbPrefix ? `${this.dbPrefix}/${path}` : path;
        
        // Add 'dev/' prefix for development environment
    //    if (isDevelopment) {
    //        prefixedPath = `dev/${prefixedPath}`;
    //    }
        
    //    console.log('Firebase path:', path, '-> prefixed:', prefixedPath, '(isDev:', isDevelopment, ')');
    //    return prefixedPath;
    //}

    // Emit an event
    async emit(eventName, data) {
        if (!this.database) {
            console.warn('Firebase database not initialized');
            return false;
        }

        try {
            const encodedEmail = this.encodeEmail(this.userEmail);
            let eventPath;

            // All events now go to events/{eventName}/{encodedEmail}
            const room = data.room ? 
            (!data.room.includes('@') && !data.room.includes('.') ? data.room : this.encodeEmail(data.room))
            : encodedEmail;
            eventPath = this.getPath(`events/${eventName}/${room}`);
            
            // Clean the data to remove undefined values and ensure user_email is set
            const cleanData = JSON.parse(JSON.stringify({
                ...data,
                room: this.decodeEmail(room),  // Store decoded room in data
                namespace: data.namespace || '/',
                timestamp: serverTimestamp(),
                event_name: eventName,  // Always include event_name for routing
                type: data.type,  // Keep group_chat_message type for messages
                user_email: data.user_email || this.userEmail // Preserve passed user_email or use default
            }));

            console.log(`Emitting ${eventName} to Firebase:`, {
                path: eventPath,
                data: cleanData
            });

            const eventRef = ref(this.database, eventPath);
            const newEventRef = push(eventRef);
            await set(newEventRef, cleanData);

            return true;
        } catch (error) {
            console.error(`Error emitting ${eventName} to Firebase:`, error);
            return false;
        }
    }

    // Generic event listener with proper reference tracking
    on(eventName, callback, options = {}) {
        if (!this.database || !callback) {
            console.warn('Firebase database not initialized or callback not provided');
            return;
        }

        try {
            // Bind the instance for use in callbacks
            const instance = this;
            const encodedEmail = instance.encodeEmail(instance.userEmail);
            let eventPath;
            //options.once = ['group_chat_message','email_review_request','agent_message','message'].includes(eventName) ? false : options.once;

            // Special handling for initialization events
            if (eventName === 'agents_initialized' || eventName === 'initialization_error' || 
                eventName === 'start_campaign' || eventName === 'campaign_started' || eventName === 'resume_started' ||
                eventName === 'force_logout') {
                
                eventPath = this.getPath(`events/${eventName}/${encodedEmail}`);
                console.log(`Setting up listener for ${eventName} at path: ${eventPath}`);
                
                const eventRef = ref(instance.database, eventPath);
                const eventQuery = query(eventRef, orderByChild('sortTime')); //make it realtime and limit to last 50 messages : NOte: we may increase this in the future

                const handler = onValue(eventQuery, (snapshot) => {
                    const rawData = snapshot.val();

                    if (!rawData) {
                        console.log(`No data exists at path: ${eventPath}`);
                        return;
                    }
                    console.log(`Raw Firebase event received for ${eventName}:`, {
                        path: eventPath,
                        data: rawData,
                        exists: snapshot.exists(),
                        rawData
                    });

                    if (!snapshot.exists()) {
                        console.log(`No data exists at path: ${eventPath}`);
                        return;
                    }

                    let dataToProcess = rawData;

                    // If it's a push ID format, get only the latest event
                    if (typeof rawData === 'object' && !Array.isArray(rawData)) {
                        const events = Object.entries(rawData)
                            .filter(([key]) => key.startsWith('-'))
                            .sort((a, b) => (a[1]?.sortTime ?? 0) - (b[1]?.sortTime ?? 0) || a[0].localeCompare(b[0]));
                        
                        if (events.length > 0) {
                            const [pushIdKey, eventData] = events[events.length - 1];
                            console.log(`Using latest event data from push ID ${pushIdKey}:`, eventData);
                            dataToProcess = eventData;
                        }
                    }

                    // Check if event is stale (do NOT skip for agents_initialized, or group_chat_message)
                    // if (eventName !== 'campaign_started' && eventName !== 'agents_initialized' && eventName !== 'group_chat_message' && instance.isEventStale(dataToProcess)) {
                    if (instance.isEventStale(dataToProcess)) {
                        console.log(`Stale event received for ${eventName}:`, dataToProcess);
                        return;
                    }

                    // Format data to match expected structure
                    const formattedData = {
                        ...dataToProcess,  // Keep all original fields
                        success: dataToProcess.success !== false,  // Default to true unless explicitly false
                        message: dataToProcess.message || '',
                        campaign_id: dataToProcess.campaign_id,
                        agent_id: dataToProcess.agent_id,  // Explicitly preserve agent_id
                        type: dataToProcess.type || eventName,
                        timestamp: dataToProcess.timestamp,
                        room: dataToProcess.room,
                        namespace: dataToProcess.namespace || '/',
                        delivered_to_socketio: dataToProcess.delivered_to_socketio,
                        // Validate campaign status fields - if success is false, force status to stopped
                        status: dataToProcess.success === false ? 'stopped' : 
                               ['active', 'stopped', 'paused'].includes(dataToProcess.status) ? dataToProcess.status : 'active',
                        agents: Array.isArray(dataToProcess.agents) ? dataToProcess.agents : [],
                        error: dataToProcess.error || null
                    };
                    console.log(`Calling handler for ${eventName} with formatted data:`, formattedData);
                    callback(formattedData);
                }, { onlyOnce: options.once });

                // Store reference for cleanup
                if (!this.eventRefs.has(eventName)) {
                    this.eventRefs.set(eventName, new Map());
                }
                const eventHandlers = this.eventRefs.get(eventName);
                eventHandlers.set(callback, { ref: eventRef, handler });
                this.listeners.set(eventName, handler);
                return;
            }

            // For email_review_request, we need to listen to both room-specific and general paths
            if (eventName === 'email_review_request') {
            //if (eventName === 'email_review_request' || eventName === 'group_chat_message') {
            // Listen to room-specific path - ensure room is encoded if it contains email
                const room = options.room ? 
                    (options.room.includes('@') ? instance.encodeEmail(options.room) : options.room) 
                    : encodedEmail;
                eventPath = this.getPath(`events/${eventName}/${room}`);
                
                // Also listen to the general path under the user's encoded email
                const generalPath = this.getPath(`events/${eventName}/${encodedEmail}`);
                const generalRef = ref(this.database, generalPath);
                
                // Track first arrival timestamps for deduplication
                const firstArrivals = new Map();
                
                const handleEmailReview = (data, source) => {
                    if (!data) return;
    
                    // Convert data to array and add metadata
                    const reviews = Object.entries(data)
                        .map(([eventId, eventData]) => ({
                            eventId,
                            ...eventData,
                            timestamp: (typeof eventData.timestamp === 'object' ? (eventData.sortTime || 0) : eventData.timestamp) || 0,
                            source,
                            sortKey: `${eventData.timestamp || Date.now()}-${eventId}` // Composite key for stable sorting
                        }));
    
                    // Sort by timestamp first, then by eventId for stability
                    reviews.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    
                    // Process reviews in chronological order with deduplication
                    for (const review of reviews) {
                        const eventKey = `${eventName}:${review.eventId}`;
                        
                        // Skip if already processed in this session
                        // Ensure processedEvents is a Set
                        if (!instance.processedEvents || !(instance.processedEvents instanceof Set)) {
                            console.warn('processedEvents is not a Set, reinitializing');
                            instance.processedEvents = new Set();
                        }
                        if (instance.processedEvents.has(eventKey)) {
                            continue;
                        }
                        
                        // Check first arrival source
                        if (firstArrivals.has(eventKey)) {
                            const firstArrival = firstArrivals.get(eventKey);
                            // Only process from original source
                            if (firstArrival.source !== source) {
                                continue;
                            }
                            // Check if enough time has passed since first arrival
                            const processingDelay = Date.now() - firstArrival.timestamp;
                            if (processingDelay < 1000) { // 1 second buffer for processing
                                continue;
                            }
                        }
                        
                        // Check if event is fresh
                        if (!instance.isEventStaleV2(review)) {
                            // Record first arrival if not seen before
                            if (!firstArrivals.has(eventKey)) {
                                firstArrivals.set(eventKey, {
                                    timestamp: Date.now(),
                                    source,
                                    eventId: review.eventId
                                });
                            }
                            
                            // Mark as processed and notify
                            instance.processedEvents.add(eventKey);
                            callback({
                                ...review,
                                requiresApproval: true,
                                processedAt: Date.now()
                            });
                            
                            // Enforce blocking behavior
                            break;
                        }
                    }
                    
                    // Cleanup old first arrivals (older than 5 minutes)
                    const now = Date.now();
                    const CLEANUP_THRESHOLD = 5 * 60 * 1000; // 5 minutes
                    for (const [key, value] of firstArrivals.entries()) {
                        if (now - value.timestamp > CLEANUP_THRESHOLD) {
                            firstArrivals.delete(key);
                        }
                    }
                };
    
                // Set up handlers for both paths - limit to recent messages only
                const cutoffTime = instance.sessionStartTime - 5000; // 5 second buffer
                const generalHandler = onValue(query(generalRef, orderByChild('sortTime'), startAt(cutoffTime)), (snapshot) => {
                    handleEmailReview(snapshot.val(), 'general');
                }, { onlyOnce: false }); // Always persistent for queue events
    
                const roomRef = ref(this.database, eventPath);
                const roomHandler = onValue(query(roomRef, orderByChild('sortTime'), startAt(cutoffTime)), (snapshot) => {
                    handleEmailReview(snapshot.val(), 'room');
                }, { onlyOnce: false }); // Always persistent for queue events
    
                // Store references for cleanup
                if (!this.eventRefs.has(eventName)) {
                    this.eventRefs.set(eventName, new Map());
                }
                const eventHandlers = this.eventRefs.get(eventName);
                eventHandlers.set(callback, { 
                    ref: generalRef, 
                    handler: () => {
                        generalHandler();
                        roomHandler();
                    }
                });
            } else {
                // For other events, use standard room handling
                // Capture instance for use in callbacks
                const instance = this;
                
                // Ensure room is encoded if it contains email
                const room = options.room ? 
                    (options.room.includes('@') ? instance.encodeEmail(options.room) : options.room)
                    : encodedEmail;
                
                eventPath = this.getPath(`events/${eventName}/${room}`);
                const eventRef = ref(instance.database, eventPath);
                // Listen to all messages without filtering by time
                // The staleness check will handle filtering old messages
                const eventQuery = query(eventRef, orderByChild('sortTime'));
                const handler = onValue(eventQuery, (snapshot) => {
                    // Don't clear processed events - this causes duplicates
                    // Messages should only be processed once per session
                    const data = snapshot.val();
                    if (!data) return;
                    
                    console.debug('Firebase event received:', {
                        eventName,
                        path: eventPath,
                        data: data,
                        isStale: instance.isEventStaleV2(data)
                    });
    
                    // Only apply email review logic to actual email_review_request events
                    if (eventName === 'email_review_request') {
                        // Sort email review requests by timestamp to ensure proper ordering
                        const reviews = Object.entries(data)
                            .map(([eventId, eventData]) => ({
                                eventId,
                                ...eventData,
                                timestamp: eventData.timestamp || Date.now()
                            }))
                            .sort((a, b) => a.timestamp - b.timestamp);
        
                        // Process reviews in order
                        for (const review of reviews) {
                            const eventKey = `${eventName}:${review.eventId}`;
                            // Ensure processedEvents is a Set
                            if (!instance.processedEvents || !(instance.processedEvents instanceof Set)) {
                                console.warn('processedEvents is not a Set, reinitializing');
                                instance.processedEvents = new Set();
                            }
                            if (!instance.processedEvents.has(eventKey) && !instance.isEventStaleV2(review)) {
                                instance.processedEvents.add(eventKey);
                                // Add blocking flag to indicate this needs user approval
                                callback({
                                    ...review,
                                    requiresApproval: true
                                });
                                // Only process one review at a time to enforce blocking behavior
                                break;
                            }
                        }
                    } else if (eventName === 'group_chat_message' || eventName === 'agent_message' || eventName === 'email_sent' || eventName === 'metrics_update') {
                        // orderByChild always returns a collection, even for single messages
                        // Process all messages in the collection
                        console.log(`[FIREBASE] Processing ${eventName} collection at path ${eventPath} with ${Object.keys(data).length} messages`, data);
                        const entries = (data && typeof data === 'object' && Object.keys(data).some(k => k.startsWith('-')))
                            ? Object.entries(data)
                            : [["__single__", data]]; // treat direct object as a single message
                        entries.forEach(([messageId, messageData]) => {
                            const messageKey = `${eventName}:${messageId}`;
                            if (!instance.processedEvents || !(instance.processedEvents instanceof Set)) {
                                instance.processedEvents = new Set();
                            }
                            const isProcessed = instance.processedEvents.has(messageKey);
                            const isStale = instance.isEventStale(messageData);
                            const contentStr = typeof messageData.content === 'object' && messageData.content !== null 
                                ? (messageData.content.content || messageData.content.text || JSON.stringify(messageData.content))
                                : messageData.content;
                            console.log(`[FIREBASE] Message ${messageId}: processed=${isProcessed}, stale=${isStale}, sortTime=${messageData.sortTime}, currentTime=${Date.now()}, content="${contentStr ? contentStr.substring(0, 50) : 'null'}..."`);
                            
                            if (!isProcessed && !isStale) {
                                instance.processedEvents.add(messageKey);
                                console.log(`[FIREBASE] Passing ${eventName} to callback:`, messageData);
                                callback(messageData);
                            } else {
                                console.log(`[FIREBASE] Skipping ${eventName} message: processed=${isProcessed}, stale=${isStale}`);
                            }
                        });
                    } else {
                        // For other single events, just check staleness and pass through
                        if (!instance.isEventStale(data)) {
                            callback(data);
                        }
                    }
                }, { onlyOnce: ['agent_message', 'message'].includes(eventName) ? false : options.once }); // Always persistent for agent/message events
    
                // Store reference for cleanup
                if (!this.eventRefs.has(eventName)) {
                    this.eventRefs.set(eventName, new Map());
                }
                const eventHandlers = this.eventRefs.get(eventName);
                eventHandlers.set(callback, { ref: eventRef, handler });
                this.listeners.set(eventName, handler);
            }
        } catch (error) {
            console.error(`Error setting up Firebase listener for ${eventName}:`, error);
        }
    }

    // Remove event listener with proper cleanup
    off(eventName, callback = null) {
        if (!this.database || !this.eventRefs.has(eventName)) return;

        const eventHandlers = this.eventRefs.get(eventName);
        
        if (callback) {
            const handlerInfo = eventHandlers.get(callback);
            if (handlerInfo) {
                handlerInfo.handler();
                eventHandlers.delete(callback);
            }
        } else {
            eventHandlers.forEach(({ handler }) => {
                handler();
            });
            eventHandlers.clear();
            this.eventRefs.delete(eventName);
            this.listeners.delete(eventName);
        }
    }

    // Room management
    async joinRoom(room) {
        if (!this.database) return false;

        try {
            const encodedUser = this.encodeEmail(this.userEmail);
            // Don't re-encode if room is already encoded (doesn't contain @ or . characters)
            const encodedRoom = !room.includes('@') && !room.includes('.') ? room : this.encodeEmail(room);
            const roomRef = ref(this.database, `rooms/${encodedRoom}/members/${encodedUser}`);
            
            await set(roomRef, {
                joined_at: new Date().toISOString(),
                active: true,
                server_timestamp: serverTimestamp()
            });

            this.rooms.add(room);  // Store original room name
            return true;
        } catch (error) {
            console.error(`Failed to join room ${room}:`, error);
            return false;
        }
    }

    async leaveRoom(room) {
        if (!this.database || !this.rooms.has(room)) return false;

        try {
            const encodedUser = this.encodeEmail(this.userEmail);
            const encodedRoom = room.includes('@') ? this.encodeEmail(room) : room;
            const roomRef = ref(this.database, `rooms/${encodedRoom}/members/${encodedUser}`);
            
            await set(roomRef, {
                left_at: new Date().toISOString(),
                active: false,
                server_timestamp: serverTimestamp()
            });

            this.rooms.delete(room);
            return true;
        } catch (error) {
            console.error(`Failed to leave room ${room}:`, error);
            return false;
        }
    }

    async cleanupCampaignState() {
        if (!this.database || !this.userEmail) return;

        const encodedEmail = this.encodeEmail(this.userEmail);
        const eventsToClean = [
            'agents_initialized',
            'campaign_started',
            'start_campaign',
            'campaign_status'
        ];

        for (const eventType of eventsToClean) {
            const eventPath = this.getPath(`events/${eventType}/${encodedEmail}`);
            const eventRef = ref(this.database, eventPath);
            
            try {
                // Clear any existing campaign status
                window.campaignStatus = { isRunning: false };
                
                // Clean up campaign state in Firebase
                await set(eventRef, null);
                
                // Reset local campaign tracking
                this.processedEvents.clear();
                
                console.log('Campaign state cleaned up successfully');
                // Remove the campaign state
                await set(eventRef, null);
                console.log('Cleaned up campaign state for user:', this.userEmail);
            } catch (error) {
                console.error('Error cleaning up campaign state:', error);
            }
        }
    }

    // Cleanup
    async cleanup() {
        // Clear the processedEvents Set
        this.processedEvents.clear();
        
        // Don't automatically clean up campaign state during cleanup - causes issues with active campaigns
        // await this.cleanupCampaignState();
        
        // Clean up Firebase event handlers with null checks
        if (this.eventRefs) {
            this.eventRefs.forEach((handlers) => {
                if (handlers && typeof handlers.forEach === 'function') {
                    handlers.forEach(({ handler }) => {
                        if (handler && typeof handler === 'function') {
                            try {
                                handler();
                            } catch (error) {
                                console.warn('Error during handler cleanup:', error);
                            }
                        }
                    });
                }
            });
        }
        
        // Clean up all listeners with null checks
        if (this.listeners && typeof this.listeners.entries === 'function') {
            for (const [eventName, callback] of this.listeners.entries()) {
                if (eventName && callback) {
                    try {
                        this.off(eventName, callback);
                    } catch (error) {
                        console.warn(`Error removing listener for ${eventName}:`, error);
                    }
                }
            }
        }
        
        // Clear all collections with null checks
        if (this.listeners && typeof this.listeners.clear === 'function') {
            this.listeners.clear();
        }
        if (this.eventRefs && typeof this.eventRefs.clear === 'function') {
            this.eventRefs.clear();
        }
        if (this.rooms && typeof this.rooms.clear === 'function') {
            this.rooms.clear();
        }
    }
    
    // Method to clear processed events for a specific event type
    clearProcessedEventsForType(eventName) {
        if (!eventName) return;
        for (const key of this.processedEvents) {
            if (key.startsWith(`${eventName}:`)) {
                this.processedEvents.delete(key);
            }
        }
        console.log(`Cleared processed events for ${eventName}`);
    }
}

export default FirebaseEvents;
